'use client';

import Image from 'next/image';
import Link from 'next/link';
import { jsonToHtml } from '@contentstack/json-rte-serializer';
import { usePricing } from '@/contexts/PricingContext';
import PriceDetails from '@/components/global/PriceDetails';
import { formatPrice } from '@/utils/utilities.mjs';
import { useVariantAware } from '@/utils/product/use-variant-aware';
import ProductCardStars from './ProductCardStars';
import { buildContentstackImageUrl } from '@/lib/utils';
import CtaArrowIcon from '@/components/icons/CtaArrowIcon';
import CheckCircleIcon from '@/components/icons/CheckCircleIcon';

const FIRMNESS_TOTAL = 5;

const ProductCard = ({ card }) => {
  const pricing = usePricing();
  const {
    button_label,
    product_image,
    title,
    product_description,
    firmness,
    features,
    shopifyProduct,
    size_label,
    product_reference,
  } = card;

  const productDescription = jsonToHtml(product_description) || '';

  const variant = useVariantAware(shopifyProduct);
  const url = product_reference?.[0]?.url || '#';
  const prices = variant ? pricing.variant(variant) : null;

  // Same as Helix ProductCard PricingInformation prefix: Shopify Size option + ": "
  // (CMS size_label overrides when authors set it; otherwise avoids empty labels).
  const variantSizeLabel =
    variant?.selectedOptions?.find((o) => o.name === 'Size')?.value ?? '';
  const cmsSizeLabel =
    typeof size_label === 'string' ? size_label.trim() : '';
  const priceSizePrefix = (cmsSizeLabel || variantSizeLabel).trim();

  return (
    <div className="flex flex-col overflow-hidden bg-white">
      {product_image?.url && (
        <div className="lg:h-40 xl:h-62.5 overflow-hidden">
          <Image
            src={buildContentstackImageUrl(product_image.url, 768)}
            alt={product_image.alt || product_image.title}
            width={760}
            height={420}
            className="w-full h-auto"
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
          />
        </div>
      )}

      <div className="flex flex-col p-6 gap-4 border-b border-neutral-200 flex-1">
        <div className="flex flex-col gap-1">
          <h3 className="text-2xl font-cormorant text-primary font-bold capitalize">
            {title}
          </h3>
          <ProductCardStars
            productId={product_reference?.[0]?.shopify_product_id}
            productPath={url}
          />
          {productDescription && (
            <div className="text-base text-primary" dangerouslySetInnerHTML={{ __html: productDescription }} />
          )}
        </div>

        {firmness > 0 && (
          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-center gap-2">
              <span className="text-sm capitalize w-1/3 text-primary">Firmness</span>
              <div className="flex gap-0.5 w-2/3">
                {Array.from({ length: FIRMNESS_TOTAL }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 w-1/5 ${i < firmness ? 'bg-primary' : 'bg-gray-200'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {features?.length > 0 && (
          <div className="flex flex-col gap-1">
            {features.map((feature, i) => {
              const featureHtml = jsonToHtml(feature.content);
              if (!featureHtml) return null;
              return (
                <div key={i} className="flex gap-2 items-start text-sm text-primary">
                  <CheckCircleIcon />
                  <div dangerouslySetInnerHTML={{ __html: featureHtml }} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* BL-815: the price and the CTA need 268px on a 320px viewport, where the
          card is only 240 wide, so the button was being clipped. The button
          stays beside the price at every width — it never drops to a second
          line — so the room comes from shrinking both below 360, the way
          production does, and from letting the price column shrink past its
          content with min-w-0. */}
      <div className="flex justify-between items-center gap-x-2 py-4 px-4 min-[360px]:gap-x-3 min-[360px]:px-6 mt-auto">
        <div className="flex min-w-0 flex-col gap-1">
          {prices !== null && (
            <>
              <span className="text-sm text-primary">Starting at</span>
              <span className="text-base min-[360px]:text-xl text-primary font-semibold">
                {priceSizePrefix && `${priceSizePrefix}: `}
                {formatPrice(prices.sale)}
              </span>
              {prices.hasPriceDifference && (
                <span className="text-primary line-through text-sm">
                  ({formatPrice(prices.full)})
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col items-end gap-1">
          <Link
            href={url}
            data-cy="product-card-link"
            className="group flex shrink-0 items-center px-3 py-2 text-sm min-[360px]:px-6 min-[360px]:py-3 min-[360px]:text-base xl:text-lg no-underline text-center bg-sea text-white rounded-full font-normal capitalize whitespace-nowrap"
          >
            {button_label || 'Shop Now'}
            <CtaArrowIcon />
          </Link>
          <PriceDetails />
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
